import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import PromoBanners from './components/PromoBanners';
import PromoProductGrid from './components/PromoProductGrid';
import PromoLoyalty from './components/PromoLoyalty';
import PromoFlashDeals from './components/PromoFlashDeals';
import PromoCodesSection from './components/PromoCodesSection';
import PromoDetailCards from './components/PromoDetailCards';
import PromoDealOfDay from './components/PromoDealOfDay';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const promosJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'OfferCatalog',
  name: 'Акции и скидки NeonPharma',
  description: 'Лучшие предложения на лекарства и витамины. Скидки до 50%, промокоды и бонусные баллы.',
  url: `${SITE_URL}/promos`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Акции', item: `${SITE_URL}/promos` },
    ],
  },
};

/* ── Hero countdown ── */
const MAIN_PROMO_END = '2026-04-12T23:59:59';

const calcTime = (endsAt: string) => {
  const diff = Math.max(0, new Date(endsAt).getTime() - Date.now());
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

const pad = (n: number) => String(n).padStart(2, '0');

const HeroCountdown = () => {
  const [time, setTime] = useState(() => calcTime(MAIN_PROMO_END));
  useEffect(() => {
    const t = setInterval(() => setTime(calcTime(MAIN_PROMO_END)), 1000);
    return () => clearInterval(t);
  }, []);

  const units = [
    { val: time.d, label: 'дней' },
    { val: time.h, label: 'часов' },
    { val: time.m, label: 'минут' },
    { val: time.s, label: 'секунд' },
  ];

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-mono text-xs text-white/40 mr-1">До конца акции:</span>
      {units.map((u, i) => (
        <div key={u.label} className="flex items-center gap-2">
          <div
            className="flex flex-col items-center px-3 py-2 rounded-xl min-w-[52px]"
            style={{ background: 'rgba(255,45,120,0.12)', border: '1px solid rgba(255,45,120,0.35)' }}
          >
            <span
              className="font-orbitron font-black text-2xl leading-none tabular-nums"
              style={{ color: '#ff2d78', textShadow: '0 0 12px rgba(255,45,120,0.8)' }}
            >
              {pad(u.val)}
            </span>
            <span className="font-mono text-[9px] mt-0.5 text-white/30">{u.label}</span>
          </div>
          {i < units.length - 1 && (
            <span className="font-orbitron font-bold text-xl text-white/20 mb-4">:</span>
          )}
        </div>
      ))}
    </div>
  );
};

const PromoPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title="Акции и скидки на лекарства — NeonPharma | Скидки до 50%"
        description="Акции интернет-аптеки NeonPharma: скидки до 50% на лекарства, витамины и БАД. Промокоды, flash-распродажи, бонусные баллы. Обновляется ежедневно."
        keywords="акции аптека, скидки лекарства, промокод NeonPharma, распродажа витамины"
        canonical="/promos"
        jsonLd={promosJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-12 px-6 lg:px-12 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/eb12e33dad65aac533b803a74f1fb203.jpg"
            alt="Акции"
            className="w-full h-full object-cover object-top opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#050508]" />
        </div>
        {/* Glow blobs */}
        <div className="absolute top-10 left-1/4 w-80 h-80 rounded-full opacity-10" style={{ background: '#ff2d78', filter: 'blur(100px)' }} />
        <div className="absolute top-0 right-1/4 w-64 h-64 rounded-full opacity-8" style={{ background: '#f0ff00', filter: 'blur(90px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 font-mono text-xs text-white/30 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <span style={{ color: '#00f5ff' }}>Акции</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl"
                  style={{ background: 'rgba(255,45,120,0.15)', border: '1px solid rgba(255,45,120,0.4)' }}
                >
                  <i className="ri-price-tag-3-line text-lg" style={{ color: '#ff2d78' }} />
                </div>
                <p className="font-mono text-xs tracking-[0.3em]" style={{ color: 'rgba(0,245,255,0.7)' }}>СПЕЦИАЛЬНЫЕ ПРЕДЛОЖЕНИЯ</p>
              </div>
              <h1 className="font-orbitron font-black text-4xl md:text-6xl text-white leading-tight">
                АКЦИИ И<br />
                <span style={{ color: '#ff2d78', textShadow: '0 0 30px #ff2d7880' }}>СКИДКИ</span>
              </h1>
              <p className="font-mono text-sm mt-3 max-w-lg" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Лучшие предложения на лекарства и витамины. Скидки до 50%, промокоды и бонусные баллы.
              </p>

              {/* Live countdown */}
              <div className="mt-6">
                <HeroCountdown />
              </div>
            </div>

            {/* Search button */}
            <div className="mt-5">
              <Link
                to="/promos/search"
                className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all duration-300"
                style={{ border: '1px solid rgba(0,245,255,0.35)', color: '#00f5ff', background: 'rgba(0,245,255,0.06)' }}
              >
                <i className="ri-search-line" />
                Поиск и фильтрация акций
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

          {/* Stats row */}
            <div className="flex gap-4 flex-wrap">
              {[
                { val: '3', label: 'Активных акции', color: '#ff2d78' },
                { val: '50%', label: 'Макс. скидка', color: '#39ff14' },
                { val: '8', label: 'Промокодов', color: '#00f5ff' },
                { val: '3', label: 'Flash-сделки', color: '#ff6b00' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="px-5 py-3 rounded-xl text-center"
                  style={{ background: `${s.color}10`, border: `1px solid ${s.color}25` }}
                >
                  <div className="font-orbitron font-black text-2xl" style={{ color: s.color, textShadow: `0 0 10px ${s.color}60` }}>
                    {s.val}
                  </div>
                  <div className="font-mono text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sections in order */}
      <PromoBanners />
      <PromoDetailCards />
      <PromoDealOfDay />
      <PromoFlashDeals />
      <PromoCodesSection />
      <PromoProductGrid />
      <PromoLoyalty />

      <Footer />
    </div>
  );
};

export default PromoPage;
